import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useMutation } from '@apollo/client';
import { useSetRecoilState, useRecoilValue } from 'recoil';

import { ButtonSlim, ButtonLink } from '@Calix-Commerce/design-system/components';

import { loadingState, notificationState, authenticationState } from 'state/atoms';
import { SEND_CONTACT_MESSAGE } from 'connectors/mutations/contact';
import { useNotifyError } from 'utils/hooks';

const ContactFormStyled = styled.div`
  width: 380px;
  padding: 1.07rem;
`;

const Select = styled.select`
  display: block;
  margin-bottom: 0.5rem;
`;

const Label = styled.p`
  margin-top: 0;
  margin-bottom: 0.5rem;
`;

const Title = styled(Label)`
  font-size: 1.3rem;
  line-height: 1.2;
  margin-top: 0;
  margin-bottom: 0.8rem;
`;

const Textarea = styled.textarea`
  display: block;
  margin-bottom: 1rem;
  width: 100%;
  height: 237px;
`;

// Contact subjects
const PRODUCT_QUESTION_OPTION = 'ProductQuestion';
const MY_CALIX_SUPPORT_OPTION = 'MyCalixStoreSupport';
const OTHER_OPTION = 'Other';

const ContactForm = ({ accountLead, handleClose }) => {
  const setLoading = useSetRecoilState(loadingState);
  const { endCustomerAccountId } = useRecoilValue(authenticationState);

  const setNotification = useSetRecoilState(notificationState);
  const notifyError = useNotifyError({
    fallbackMessage: 'Unable to send message.',
  });

  const [subject, setSubject] = useState(PRODUCT_QUESTION_OPTION);
  const [message, setMessage] = useState('');

  const [sendContactMessage, { loading }] = useMutation(SEND_CONTACT_MESSAGE);

  useEffect(() => {
    setLoading(loading);
  }, [loading, setLoading]);

  const notifyEmptyMessage = () => {
    setNotification({
      type: 'error',
      show: true,
      text: 'Please insert a message to send to your Customer Operations Representative',
    });
  };

  const notifyMessageSuccess = () => {
    setNotification({
      type: 'success',
      show: true,
      text: 'Thank you! Your request has been sent to your Customer Operations Representative.',
      timeout: 4000,
    });
  };

  const handleSendClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!message) {
      notifyEmptyMessage();
    } else {
      const { pathname, href } = window.location;
      const orderNumber = pathname.split('/').pop();
      const isOnOrderPage = pathname === `/order-manager/orders/${orderNumber}`;
      const contextUrl = isOnOrderPage ? href : null;

      const contextData = [
        {
          name: 'activeRecord',
          value: contextUrl,
        },
        { name: 'context', value: 'Order Manager' },
      ];
      sendContactMessage({
        variables: {
          messageInput: {
            accountId: endCustomerAccountId,
            recipientType: 'CustomerOperations',
            type: subject,
            content: message,
            properties: contextData,
          },
        },
      })
        .then(() => {
          notifyMessageSuccess();
          handleClose();
        })
        .catch(notifyError);
    }
  };

  return (
    <ContactFormStyled>
      <Title>Use this form to communicate with your Customer Operations Representative</Title>
      <div>
        <Label>Subject</Label>
        <Select onChange={({ target }) => setSubject(target.value)}>
          <option value={PRODUCT_QUESTION_OPTION}>Product Question</option>
          <option value={MY_CALIX_SUPPORT_OPTION}>My Calix Store Support</option>
          <option value={OTHER_OPTION}>Other</option>
        </Select>
        <Label>Message</Label>
        <Textarea onChange={({ target }) => setMessage(target.value)} />
        <ButtonSlim onClick={handleSendClick}>Send</ButtonSlim>
        <ButtonLink onClick={handleClose}>CANCEL</ButtonLink>
      </div>
    </ContactFormStyled>
  );
};
export { ContactForm };
